/**
 * THE RENTALS SIDE'S ONE WAY TO THE SERVER.
 *
 * Every rentals screen reads and writes through /api/rentals
 * (server/routes/rentals.ts), which checks who is asking from their own
 * token and scopes everything to their shop. The same bearer pattern as
 * the Needs a person page, written once here rather than four times.
 *
 * A failure comes back as { ok: false, error } with the server's sentence
 * in it, or "Could not reach the server" when there was no answer at all,
 * so every screen can put what went wrong under the button that did it.
 * `missing` is set when migration 0185 is not applied yet, which the
 * screens say in words rather than as an error.
 */

import { api, db } from "./client.ts";

export type Answer<T> = { ok: true; data: T } | { ok: false; error: string; missing?: boolean };

async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

export async function rentalsApi<T>(path: string, method: "GET" | "POST" | "PATCH" | "DELETE" = "GET", body?: unknown): Promise<Answer<T>> {
  try {
    const r = await fetch(api(`/api/rentals${path}`), {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${await bearer()}` },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const out = await r.json().catch(() => ({})) as T & { error?: string; missing?: boolean };
    if (!r.ok) return { ok: false, error: out.error ?? "That did not work.", missing: out.missing === true };
    return { ok: true, data: out };
  } catch {
    return { ok: false, error: "Could not reach the server. Try again in a moment." };
  }
}

/**
 * THE RENTER'S OWN PAGE FOR A TRIP, as a signed link on the shop's domain,
 * with a ready text to send it in. server/routes/trip-page.ts.
 */
export async function renterLink(tripId: string): Promise<Answer<{ url: string; text: string }>> {
  try {
    const r = await fetch(api(`/api/rentals-share/${encodeURIComponent(tripId)}`), {
      headers: { Authorization: `Bearer ${await bearer()}` },
    });
    const out = await r.json().catch(() => ({})) as { url?: string; text?: string; error?: string };
    if (!r.ok || !out.url) return { ok: false, error: out.error ?? "Could not make the link." };
    return { ok: true, data: { url: out.url, text: out.text ?? out.url } };
  } catch {
    return { ok: false, error: "Could not reach the server. Try again in a moment." };
  }
}
